/**
 * The one request this SPA makes that must carry **neither** the org header nor a cookie.
 *
 * It lives in `apps/app/src/api/` because that is the SPA's only door to the network —
 * c33's rule, enforced by `no-restricted-globals` on `fetch` everywhere else. It is worth
 * saying plainly why it is here rather than suppressed there, because the rule's own
 * message points the other way:
 *
 *   "That directory sets the x-packagepage-org header in exactly one function; a fetch
 *    outside it is a request for no organization or for the wrong one."
 *
 * **This request is legitimately for no organization**, and it would be a defect if it
 * were for one. It goes to the resolver edge — a different registrable domain, by the
 * invariant that never lets `packagepage.com` and `pkgedge.com` collapse — to read a
 * public description file that every conformant resolver serves to anybody. It has no
 * tenant, no identity and no authorization. Sending `x-packagepage-org` would leak which
 * organization the user is acting in to an origin that has no business knowing;
 * `credentials: "omit"` keeps the session cookie on the origin it was issued for.
 *
 * So the rule is obeyed rather than excepted: the network call is in the one directory
 * allowed to make one, and *this* file is where the reason a request may skip the header
 * is written down. Anything else added here owes the same paragraph.
 */

/** What the dot is entitled to claim. `unknown` is the state before any probe settles. */
export type EdgeLiveness = "unknown" | "live" | "unreachable";

/**
 * What a probe produced, reduced to what the decision depends on.
 *
 * Deliberately not a `Response`: passing the real object would make every test construct
 * one, and the two things this decision reads — did it answer, and was the body a
 * description file — are the whole input.
 */
export type EdgeProbe =
  | { readonly kind: "response"; readonly ok: boolean; readonly body: unknown }
  /** The fetch never completed: DNS, TLS, connection refused, offline, CORS-blocked. */
  | { readonly kind: "unreachable" };

/**
 * The endpoint. A resolver's own description file, which every conformant resolver serves
 * and which carries `access-control-allow-origin: *` because *"SHALL support CORS"* is a
 * conformance criterion — so this check is legal cross-origin by the same standard that
 * makes the edge worth checking at all. It is also the cheapest honest question
 * available: it proves the edge is serving resolver responses rather than merely
 * accepting TCP.
 */
export const EDGE_PROBE_PATH = "/.well-known/gs1resolver";

/**
 * The member that distinguishes a resolver description file from anything else that can
 * answer 200.
 *
 * `supportedPrimaryKeys` is required by GS1's description-file schema and is an array;
 * `apps/resolver/src/hotpath/description.ts` emits it on every response. **This check is
 * not defensive tidiness.** If the ping were ever pointed at a relative path it would hit
 * the app host's own SPA fallback — Caddy's `try_files {path} /index.html` — and get back
 * `200 text/html`. A dot that went green on any 200 would then report the dashboard's own
 * web server as a healthy resolver edge, which is the most confidently wrong thing it
 * could do.
 */
function isDescriptionFile(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  const keys = (body as { supportedPrimaryKeys?: unknown }).supportedPrimaryKeys;
  return Array.isArray(keys);
}

/**
 * Classify one completed probe.
 *
 * Never returns `unknown`: that is the state *before* a probe rather than an answer one
 * can produce, and collapsing the two would let "we have not asked" and "we asked and it
 * did not answer" share a spelling.
 */
export function classifyEdge(probe: EdgeProbe): Exclude<EdgeLiveness, "unknown"> {
  if (probe.kind === "unreachable") return "unreachable";
  if (!probe.ok) return "unreachable";
  return isDescriptionFile(probe.body) ? "live" : "unreachable";
}

/**
 * Run one probe. Resolves to a state and **never rejects** — the caller is a query whose
 * error branch would otherwise become a fourth state meaning the same thing as
 * `unreachable`, and two spellings of one state is how they drift apart.
 */
export async function probeEdge(resolverBaseUrl: string): Promise<EdgeLiveness> {
  let response: Response;
  try {
    response = await fetch(new URL(EDGE_PROBE_PATH, resolverBaseUrl), {
      credentials: "omit",
      headers: { accept: "application/json" },
    });
  } catch {
    return classifyEdge({ kind: "unreachable" });
  }

  if (!response.ok) return classifyEdge({ kind: "response", ok: false, body: null });

  // A non-JSON body is the SPA-fallback case above: `.json()` throws on HTML, and the
  // throw is the signal rather than an error to report.
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return classifyEdge({ kind: "response", ok: true, body });
}
